/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';
import {
  useSelectedOrganizationId,
  useAuthtoken,
  logout,
} from 'stores/useAuthStore';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  GridFilterItem,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';

import request, { getDefaultRequestOptions } from 'utils/request';
import { auditExport } from 'utils/auditExport';
import { showSnackbar } from 'stores/useUiStore';
import type { ApiRespBody } from 'types/api';
import type { TSchema } from 'types/items';
import type { Configuration, ExtGridColDef } from 'containers/Schema/schema.types';

export const TOKEN_INVALID = 'Token is not valid';

export const BASE_VIEW_QUERY_KEY = ['baseView'] as const;

export interface BaseViewParams {
  disablePagination: boolean;
  forceRefresh: boolean;
  filterModel?: GridFilterModel;
  globalSearch: string;
  paginationModel?: GridPaginationModel;
  sortModel?: GridSortModel;
}

export interface BaseViewList {
  values: any[];
  rowCount: number;
}

function buildListSearchParams(
  configuration: Configuration,
  params: BaseViewParams
): URLSearchParams {
  const {
    disablePagination,
    forceRefresh,
    filterModel,
    globalSearch,
    paginationModel,
    sortModel,
  } = params;
  const searchParams = new URLSearchParams();

  let sortString = '';
  if (sortModel?.length) {
    sortModel.forEach((sort) => {
      if (sortString) sortString += ',';
      sortString += `${sort.field}:${sort.sort}`;
    });
    searchParams.append('orderBy', sortString);
  }

  const processFilterItems = (filterItems: GridFilterItem[]) => {
    filterItems.forEach((filter) => {
      let operator = '';
      let filterValue = filter.value;

      if (filter.operator === 'equals') {
        operator = '';
      } else if (filter.operator === 'isEmpty') {
        operator = '';
        if (filter.field === 'parentNotificationID') {
          filterValue = null;
        } else {
          filterValue = '';
        }
      } else {
        operator =
          filter.operator.charAt(0).toUpperCase() + filter.operator.slice(1);
      }

      searchParams.append(`${filter.field}${operator}`, filterValue ?? '');
    });
  };

  if (filterModel?.items) {
    processFilterItems(filterModel.items);
  }
  if (configuration.inherentFilterModel?.items) {
    processFilterItems(configuration.inherentFilterModel.items);
  }

  if (globalSearch !== '') {
    let globalSearchFields = '';
    configuration.listViewColumns?.forEach((column: ExtGridColDef) => {
      if (column.excludeFromGlobalSearch) return;
      if (globalSearchFields) globalSearchFields += ',';
      globalSearchFields += column.field;
    });
    if (globalSearchFields && globalSearch) {
      searchParams.append('globalSearchFields', globalSearchFields);
      searchParams.append('globalSearchValue', globalSearch);
    }
  }

  if (!disablePagination && paginationModel) {
    searchParams.append(
      'offset',
      `${paginationModel.page * paginationModel.pageSize}`
    );
    searchParams.append('limit', `${paginationModel.pageSize + 1}`);
  }

  if (forceRefresh) {
    searchParams.append('ttlSecs', '0');
  }

  return searchParams;
}

export async function fetchBaseList(
  configuration: Configuration,
  params: BaseViewParams,
  authToken: string,
  signal?: AbortSignal
): Promise<BaseViewList> {
  const searchParams = buildListSearchParams(configuration, params);
  const result = await request<ApiRespBody<any>>(
    `/api/${configuration.itemType}?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: authToken,
      },
      ...(signal ? { signal } : {}),
    }
  );

  if (result?.success && result.data) {
    result.data.forEach((row: any, index: number) => {
      row.id = index;
    });

    const { paginationModel, disablePagination } = params;
    const pageSize = paginationModel?.pageSize ?? result.data.length;
    const page = paginationModel?.page ?? 0;
    const rowCount: number =
      result.data.length > pageSize ? -1 : result.data.length + page * pageSize;

    if (rowCount === -1 && !disablePagination) {
      result.data.pop();
    }

    return { values: result.data, rowCount };
  }

  if (result?.message === TOKEN_INVALID) {
    throw new Error(TOKEN_INVALID);
  }
  throw new Error(result?.message || 'Failed to load data');
}

export function baseViewListQueryOptions(
  configuration: Configuration,
  params: BaseViewParams | null,
  authToken: string,
  organizationId: number | string | null,
  customList?: BaseViewCustomList
) {
  return queryOptions({
    queryKey: [
      ...BASE_VIEW_QUERY_KEY,
      configuration.itemType,
      organizationId,
      configuration.inherentFilterModel ?? null,
      params,
    ],
    queryFn: ({ signal }) => {
      if (!params) throw new Error('Base view parameters are required');
      return customList
        ? customList(configuration, params, signal)
        : fetchBaseList(configuration, params, authToken, signal);
    },
    placeholderData: keepPreviousData,
    enabled: !!authToken && !!params,
  });
}

function buildAddBody(row: any, schema: TSchema<any>): any {
  const body: any = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const field in schema.fields) {
    if (
      schema.fields[field].required !== 'A' &&
      row[field] !== null &&
      row[field] !== ''
    ) {
      body[field] = row[field];
    }
  }
  return body;
}

function buildChangeBody(row: any, schema: TSchema<any>): any {
  const body: any = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const field in schema.fields) {
    if (
      schema.fields[field].required !== 'A' &&
      schema.fields[field].editable !== 'false'
    ) {
      if (row[field] === '' && schema.fields[field].required === 'N') {
        body[field] = null;
      } else {
        body[field] = row[field];
      }
    }
  }
  return body;
}

function aggregate(results: { success?: boolean; message?: string }[]): {
  success: boolean;
  message: string;
} {
  let success = true;
  let message = '';
  results.forEach((result) => {
    if (!result.success) {
      success = false;
      message += `${result.message} `;
    }
  });
  return { success, message };
}

async function addRows(
  configuration: Configuration,
  rows: any[],
  schema: TSchema<any>,
  authToken: string
) {
  const results: { success?: boolean; message?: string }[] = [];

  for (const row of rows) {
    // eslint-disable-next-line no-await-in-loop
    const result = await request<ApiRespBody<any>>(`/api/${configuration.itemType}`, {
      method: 'POST',
      body: JSON.stringify(buildAddBody(row, schema)),
      headers: { 'Content-Type': 'application/json', authorization: authToken },
    });
    results.push(result ?? { success: false, message: 'No response from server' });
  }
  return aggregate(results);
}

async function changeRows(
  configuration: Configuration,
  rows: any[],
  schema: TSchema<any>,
  authToken: string
) {
  const { hierarchy } = schema;
  const results: { success?: boolean; message?: string }[] = [];

  for (const row of rows) {
    const body = buildChangeBody(row, schema);
    if (hierarchy?.primaryKey && hierarchy.primaryKey !== '') {
      // eslint-disable-next-line no-await-in-loop
      const result = await request<ApiRespBody<any>>(
        `/api/${configuration.itemType}/${encodeURI(row[hierarchy.primaryKey])}`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json', authorization: authToken },
        }
      );
      results.push(result ?? { success: false, message: 'No response from server' });
    } else if (hierarchy?.compositePrimaryKey) {
      const keyParams = new URLSearchParams();
      hierarchy.compositePrimaryKey.forEach((key: string) => {
        if (row[key]) {
          keyParams.append(key, row[key]);
        } else if (configuration?.inherentFilterModel?.items?.length) {
          const inherentValue = configuration.inherentFilterModel.items.find(
            (item: GridFilterItem) => item.field === key
          )?.value;
          if (inherentValue) keyParams.append(key, inherentValue);
        }
      });
      // eslint-disable-next-line no-await-in-loop
      const result = await request<ApiRespBody>(
        `/api/${configuration.itemType}?${keyParams.toString()}`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json', authorization: authToken },
        }
      );
      results.push(result ?? { success: false, message: 'No response from server' });
    } else {
      results.push({ success: false, message: 'No key found for updating' });
    }
  }
  return aggregate(results);
}

async function deleteRows(
  configuration: Configuration,
  rows: any[],
  schema: TSchema<any>,
  authToken: string
) {
  const { hierarchy } = schema;
  const results: { success?: boolean; message?: string }[] = [];

  for (const row of rows) {
    if (hierarchy?.primaryKey && hierarchy.primaryKey !== '') {
      // eslint-disable-next-line no-await-in-loop
      const result = await request<ApiRespBody<any>>(
        `/api/${configuration.itemType}/${encodeURI(row[hierarchy.primaryKey])}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', authorization: authToken },
        }
      );
      results.push(result ?? { success: false, message: 'No response from server' });
    } else if (hierarchy?.compositePrimaryKey) {
      const keyParams = new URLSearchParams();
      hierarchy.compositePrimaryKey.forEach((key: string) => {
        if (row[key]) keyParams.append(key, row[key]);
      });
      // eslint-disable-next-line no-await-in-loop
      const result = await request<ApiRespBody>(
        `/api/${configuration.itemType}?${keyParams.toString()}`,
        getDefaultRequestOptions('DELETE', authToken)
      );
      results.push(result ?? { success: false, message: 'No response from server' });
    } else {
      results.push({ success: false, message: 'No key found for delete' });
    }
  }
  return aggregate(results);
}

function exportRowsToCsv(
  configuration: Configuration,
  rows: any[],
  authToken: string
) {
  if (!rows.length) return;
  const headers: string[] = Object.keys(rows[0]);
  let csv = `${headers.join(',')}\n`;
  rows.forEach((row) => {
    headers.forEach((header) => {
      csv += `${(row[header] || '').toString().replace(/,/g, ';')},`;
    });
    csv += '\n';
  });

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Harmoni ${configuration.itemLabel} Export ${new Date().toISOString()}.csv`;
  a.click();
  URL.revokeObjectURL(url);

  auditExport(
    {
      resource_type: configuration.itemType,
      record_count: rows.length,
      export_format: 'csv',
      result_size_bytes: blob.size,
    },
    authToken
  );
}

export type BaseViewCustomList = (
  configuration: Configuration,
  params: BaseViewParams,
  signal?: AbortSignal
) => Promise<BaseViewList>;

export interface BaseViewDataOptions {
  customAdd?: (
    rows: any[],
    schema: TSchema<any>,
    configuration: Configuration
  ) => Promise<void>;
  customChange?: (
    rows: any[],
    schema: TSchema<any>,
    configuration: Configuration
  ) => Promise<void>;
  customDelete?: (
    rows: any[],
    schema: TSchema<any>,
    configuration: Configuration
  ) => Promise<void>;
  customList?: BaseViewCustomList;
  onMutationSuccess?: () => void;
}

export interface BaseViewMutationVars {
  rows: any[];
  schema: TSchema<any>;
}

export function useBaseViewData(
  configuration: Configuration,
  params: BaseViewParams | null,
  authToken: string,
  options?: BaseViewDataOptions
) {
  const queryClient = useQueryClient();
  const organizationId = useSelectedOrganizationId();

  const listQuery = useQuery(
    baseViewListQueryOptions(
      configuration,
      params,
      authToken,
      organizationId,
      options?.customList
    )
  );

  useEffect(() => {
    if (
      listQuery.error instanceof Error &&
      listQuery.error.message === TOKEN_INVALID
    ) {
      logout();
    }
  }, [listQuery.error]);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: [...BASE_VIEW_QUERY_KEY, configuration.itemType],
    });

  const add = useMutation({
    mutationFn: async ({ rows, schema }: BaseViewMutationVars) => {
      if (options?.customAdd) {
        await options.customAdd(rows, schema, configuration);
        return { custom: true as const };
      }
      return addRows(configuration, rows, schema, authToken);
    },
    onSuccess: (result) => {
      if (result && 'custom' in result) {
        options?.onMutationSuccess?.();
        return;
      }
      const { success, message } = result;
      showSnackbar(
          success
            ? {
                autoHideDuration: 5000,
                caller: 'baseView.add',
                open: true,
                message: 'Row(s) Have Been Added',
                severity: 'success',
              }
            : {
                autoHideDuration: null,
                caller: 'baseView.add',
                open: true,
                message,
                severity: 'error',
              }
        );
      if (success) options?.onMutationSuccess?.();
    },
    onSettled: invalidate,
  });

  const change = useMutation({
    mutationFn: async ({ rows, schema }: BaseViewMutationVars) => {
      if (options?.customChange) {
        await options.customChange(rows, schema, configuration);
        return { custom: true as const };
      }
      return changeRows(configuration, rows, schema, authToken);
    },
    onSuccess: (result) => {
      if (result && 'custom' in result) {
        options?.onMutationSuccess?.();
        return;
      }
      const { success, message } = result;
      showSnackbar(
          success
            ? {
                autoHideDuration: 5000,
                caller: 'baseView.change',
                open: true,
                message: 'Row(s) Have Been Updated',
                severity: 'success',
              }
            : {
                autoHideDuration: null,
                caller: 'baseView.change',
                open: true,
                message,
                severity: 'error',
              }
        );
      if (success) options?.onMutationSuccess?.();
    },
    onSettled: invalidate,
  });

  const remove = useMutation({
    mutationFn: async ({ rows, schema }: BaseViewMutationVars) => {
      if (options?.customDelete) {
        await options.customDelete(rows, schema, configuration);
        return { custom: true as const };
      }
      return deleteRows(configuration, rows, schema, authToken);
    },
    onSuccess: (result) => {
      if (result && 'custom' in result) {
        options?.onMutationSuccess?.();
        return;
      }
      const { success, message } = result;
      showSnackbar(
          success
            ? {
                autoHideDuration: 5000,
                caller: 'baseView.delete',
                open: true,
                message: 'Selected Rows Have Been Deleted',
                severity: 'success',
              }
            : {
                autoHideDuration: null,
                caller: 'baseView.delete',
                open: true,
                message,
                severity: 'error',
              }
        );
      if (success) options?.onMutationSuccess?.();
    },
    onSettled: invalidate,
  });

  const exportRows = (rows: any[]) =>
    exportRowsToCsv(configuration, rows, authToken);

  return { listQuery, add, change, remove, exportRows, invalidate };
}

export function useBaseViewAuthToken() {
  return useAuthtoken();
}
